import { isISODateBetween, todayISO } from "./formUtils";

/**
 * isQuestionValid
 * ---------------
 * Validates a single question against current formData.
 *
 * IMPORTANT:
 * - Your UI does not rely on native HTML form validation, so constraints like
 *   `minLength`, `maxLength`, and `pattern` must be enforced here.
 *
 * @param {object} question
 * @param {Object<string, *>} formData
 * @returns {boolean}
 */
export const isQuestionValid = (question, formData) => {
  const value = formData[question.id];

  // Non-required always valid
  if (!question.required) return true;

  switch (question.fieldType) {
    case "boolean-matrix": {
      if (!Array.isArray(question.rows) || question.rows.length === 0)
        return true;
      if (value == null || typeof value !== "object") return false;

      return question.rows.every((r) => {
        const v = value[r.id];
        return v !== undefined && v !== null && String(v) !== "";
      });
    }

    case "range":
      // allow 0; just not null/undefined/""
      return value !== undefined && value !== null && value !== "";

    case "date": {
      if (value === undefined || value === null || String(value) === "")
        return false;
      const MIN = "1900-01-01";
      const MAX = todayISO();
      return isISODateBetween(String(value), MIN, MAX);
    }

    case "text":
    case "textarea": {
      const s = String(value ?? "").trim();
      if (s === "") return false;

      // Enforce min/max length if present
      if (
        Number.isFinite(question.minLength) &&
        s.length < question.minLength
      ) {
        return false;
      }
      if (
        Number.isFinite(question.maxLength) &&
        s.length > question.maxLength
      ) {
        return false;
      }

      // Enforce regex pattern if present
      if (question.pattern) {
        const re = new RegExp(question.pattern);
        if (!re.test(s)) return false;
      }

      return true;
    }

    case "radio":
    case "select": {
      // must be non-empty
      return value !== undefined && value !== null && String(value) !== "";
    }

    case "number": {
      // must be present; allow 0
      if (value === undefined || value === null || String(value) === "")
        return false;

      // Optional: enforce numeric parse
      const n = Number(value);
      return Number.isFinite(n);
    }

    default:
      // generic non-empty check (0 is fine)
      return value !== undefined && value !== null && String(value) !== "";
  }
};
